import {
	STATUS_BADGE_TONE_PALETTES,
	StatusBadge,
	type StatusBadgePalette,
} from "@components";
import { dayjs } from "@lib";
import type { ReactNode } from "react";
import type { ListingDataTableColumn } from "../components/listing-data-table";
import { COMMUNICATION_STATUS_LABELS } from "../constants";
import type {
	CommunicationVehicle,
	CommunicationVehicleStatus,
} from "../types";
import { stripPlateHyphens } from "./plate.utils";

const EMPTY_VALUE_LABEL = "Vazio";

export function formatCommunicationEmptyValue(
	value: string | null | undefined
): string {
	if (!value?.trim()) {
		return EMPTY_VALUE_LABEL;
	}

	return value.trim();
}

export function formatCommunicationDateTime(
	dateString: string | null | undefined
): string {
	if (!dateString) {
		return "";
	}

	const date = dayjs(dateString);
	return `${date.format("DD/MM/YYYY")} ${date.format("HH:mm")}`;
}

export function formatCommunicationStatus(
	status: CommunicationVehicleStatus
): string {
	return COMMUNICATION_STATUS_LABELS[status] ?? status;
}

const STATUS_BADGE_PALETTES: Record<
	CommunicationVehicleStatus,
	StatusBadgePalette
> = {
	CONNECTED: STATUS_BADGE_TONE_PALETTES.success,
	DISCONNECTION: STATUS_BADGE_TONE_PALETTES.error,
};

function CommunicationStatusBadge({
	status,
}: {
	status: CommunicationVehicleStatus;
}) {
	return (
		<StatusBadge
			label={formatCommunicationStatus(status)}
			palette={STATUS_BADGE_PALETTES[status] ?? STATUS_BADGE_TONE_PALETTES.gray}
		/>
	);
}

export function renderCommunicationCell(
	item: CommunicationVehicle,
	column: ListingDataTableColumn<CommunicationVehicle>
): ReactNode {
	switch (column.id) {
		case "groupName":
			return formatCommunicationEmptyValue(item.groupName);

		case "plate":
			return stripPlateHyphens(item.plate);

		case "model":
			return formatCommunicationEmptyValue(item.model);

		case "status":
			return <CommunicationStatusBadge status={item.status} />;

		case "lastPosition": {
			if (!item.lastPosition) {
				return "";
			}

			const date = dayjs(item.lastPosition);
			return (
				<div className="flex flex-col text-sm">
					<span>{date.format("DD/MM/YYYY")}</span>
					<span>{date.format("HH:mm")}</span>
				</div>
			);
		}

		default:
			return "";
	}
}
